import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Rocket, ShoppingBag, Check, Copy, Share2, Lock, Trophy, Sparkles, Clock, Crown, Film as FilmIcon, ArrowLeft, Info, Truck, Flame, ShoppingCart } from "lucide-react";
import { api } from "../lib/api";
import ProgressBar from "../components/ProgressBar";
import LaunchButton from "../components/LaunchButton";
import ProductMedia from "../components/ProductMedia";
import useEmbedResize from "../hooks/useEmbedResize";

/**
 * Public storefront preview.
 *
 * When /storefront is hit without an id, lists campaigns to pick from
 * (simulates Theme App Extension preview).
 */
export default function Storefront() {
  useEmbedResize();
  const { id } = useParams();
  if (!id) return <StorefrontList />;
  return <ProductPage id={id} />;
}

export { StorefrontList, ReelStorefrontCard, RoundCard, timeLeftLabel };

const PAGE_SIZE = 15;
const STORE_EMAIL_KEY = "launchpad:storefront:email";
const VOTED_KEY = "launchpad:storefront:voted"; // map of campaignId -> true

function loadVotedMap() {
  try {
    return JSON.parse(localStorage.getItem(VOTED_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveVotedMap(map) {
  try { localStorage.setItem(VOTED_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

function StorefrontList({ statusFilter } = {}) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(() => localStorage.getItem(STORE_EMAIL_KEY) || "");
  const [emailDraft, setEmailDraft] = useState("");
  const [voted, setVoted] = useState(() => loadVotedMap());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const refresh = async () => {
    const list = await api.publicListCampaigns();
    setCampaigns(list);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const saveEmail = (e) => {
    e?.preventDefault();
    const clean = emailDraft.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email");
      return;
    }
    setEmail(clean);
    localStorage.setItem(STORE_EMAIL_KEY, clean);
    toast.success("Email saved — launch as many as you like!");
  };

  const changeEmail = () => {
    localStorage.removeItem(STORE_EMAIL_KEY);
    localStorage.removeItem(VOTED_KEY);
    setEmail("");
    setEmailDraft("");
    setVoted({});
  };

  const handleVote = async (campaignId) => {
    if (!email) {
      toast.error("Add your email at the top to launch products");
      document.getElementById("storefront-email-input")?.focus();
      return;
    }
    try {
      const r = await api.vote(campaignId, email);
      const nextVoted = { ...voted, [campaignId]: true };
      setVoted(nextVoted);
      saveVotedMap(nextVoted);
      if (!r.ok && r.reason === "already_voted") {
        toast("You're already in on this launch");
      } else if (r.thresholdHit) {
        toast.success("🚀 Threshold hit — pre-orders open!");
      } else {
        toast.success("You're in!");
      }
      // Optimistic + refresh underlying counts
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === campaignId && r.ok
            ? {
                ...c,
                currentVoteCount: r.currentVoteCount,
                status: r.thresholdHit ? "pre_order" : c.status,
              }
            : c
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not launch this");
    }
  };

  // Solo launches grid hides round-tied campaigns (they live on their round page)
  // BUT the pre-orders grid explicitly includes winners of finished rounds
  // — once a contest ends, its winners flip to pre_order and belong here too.
  const filtered = campaigns
    .filter((c) => (!c.roundId || c.status === "pre_order" || c.status === "launched"))
    .filter((c) => (statusFilter ? c.status === statusFilter : true));
  const standaloneCampaigns = filtered;
  const visible = standaloneCampaigns.slice(0, visibleCount);
  const remaining = Math.max(0, standaloneCampaigns.length - visibleCount);

  return (
    <div className="dc-store bg-[#F2EFE9]" data-testid="storefront-list">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="text-xs text-[#3A3733] uppercase tracking-widest font-bold" data-testid="campaign-counter">
            Showing <strong>{visible.length}</strong> of <strong>{standaloneCampaigns.length}</strong>
          </div>
        </div>

        {/* Email bar — voting only; hidden on pre-order / just-dropped feeds */}
        {statusFilter !== "pre_order" && statusFilter !== "launched" && (
        <div className="lp-card p-5 mt-6" data-testid="storefront-email-bar">
          {email ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <Check size={16} className="text-emerald-600" />
                Launching as <strong data-testid="active-voter-email">{email}</strong>
              </div>
              <button
                onClick={changeEmail}
                className="lp-btn-secondary text-xs"
                data-testid="change-email-btn"
              >
                Change email
              </button>
            </div>
          ) : (
            <form onSubmit={saveEmail} className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <label className="lp-label" htmlFor="storefront-email-input">Your email</label>
                <input
                  id="storefront-email-input"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="lp-input"
                  data-testid="storefront-email-input"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                />
                <div className="lp-help">Add it once — we&apos;ll use it for every launch you support on this page.</div>
              </div>
              <button type="submit" className="lp-btn-primary" data-testid="save-email-btn">
                Save & start launching
              </button>
            </form>
          )}
        </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="storefront-grid">
          {loading ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12">Loading…</div>
          ) : campaigns.length === 0 ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12">
              No campaigns yet. <Link to="/app/campaigns/new" className="underline">Create one</Link>.
            </div>
          ) : (
            visible.map((c) => (
              <StoreCard
                key={c.id}
                c={c}
                voted={!!voted[c.id]}
                onVote={() => handleVote(c.id)}
              />
            ))
          )}
        </div>

        {/* Load more */}
        {remaining > 0 && (
          <div className="flex justify-center mt-8">
            <button
              className="lp-btn-secondary"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              data-testid="load-more-btn"
            >
              Load {Math.min(remaining, PAGE_SIZE)} more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReelStorefrontCard({ reel }) {
  return (
    <Link
      to={`/storefront/reels/${reel.id}`}
      className="relative rounded-2xl overflow-hidden block group border border-[#E4E1DA] hover:border-[#111] transition-all aspect-[9/16] bg-[#111]"
      data-testid={`storefront-reel-card-${reel.id}`}
    >
      {/* Poster image (if available) — first campaign's product image */}
      {reel.campaigns && reel.campaigns[0]?.productImageUrl && (
        <img
          src={reel.campaigns[0].productImageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[#d0473e] text-[10px] uppercase tracking-widest font-semibold">
        <FilmIcon size={11} /> Reel
      </div>
      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white text-xs font-semibold group-hover:scale-110 transition-transform">
        ▶
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        <h3 className="text-base font-semibold tracking-tight leading-snug group-hover:underline line-clamp-2">{reel.name}</h3>
        <div className="mt-2 flex items-center gap-3 text-[11px] text-white/85">
          <span className="inline-flex items-center gap-1"><Crown size={11} className="text-[#d0473e]" /> Top {reel.winnersCount}</span>
          <span className="text-white/40">·</span>
          <span className="inline-flex items-center gap-1" data-testid={`storefront-reel-time-${reel.id}`}>
            <Clock size={11} className="text-[#d0473e]" />
            {timeLeftLabel(reel.endDate)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function timeLeftLabel(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return "Ending now";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}

function RoundCard({ round }) {
  return (
    <Link
      to={`/storefront/rounds/${round.id}`}
      className="rounded-2xl p-6 block group bg-white border border-[#E4E1DA] hover:border-[#111] transition-all"
      data-testid={`storefront-round-card-${round.id}`}
    >
      <div className="flex items-center gap-2 text-[#d0473e] text-[10px] uppercase tracking-widest font-semibold">
        <Trophy size={12} /> Head-to-head
      </div>
      <h3 className="text-xl font-semibold tracking-tight mt-2 text-[#111] group-hover:underline">{round.name}</h3>
      {round.description && (
        <p className="text-sm text-[#6D7175] mt-2 line-clamp-2">{round.description}</p>
      )}
      <div className="mt-5 flex items-center gap-4 text-sm flex-wrap text-[#111]">
        <div className="flex items-center gap-1.5">
          <Crown size={14} className="text-[#d0473e]" />
          <span>Top {round.winnersCount} win</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={14} className="text-[#d0473e]" />
          <span data-testid={`storefront-round-time-${round.id}`}>{timeLeftLabel(round.endDate)}</span>
        </div>
        <span className="ml-auto text-[11px] tracking-wide px-3 py-1.5 rounded-full bg-[#111] text-white group-hover:bg-[#2C2C2C] transition-colors">
          Vote now →
        </span>
      </div>
    </Link>
  );
}


function StoreCard({ c, voted, onVote }) {
  const pct = Math.min(100, Math.round((c.currentVoteCount / Math.max(1, c.voteThreshold)) * 100));
  const isPreOrder = c.status === "pre_order";
  const isLaunched = c.status === "launched";
  const isClosed = c.status === "closed" || c.status === "fulfilled";

  return (
    <div className="lp-card lp-card-hover overflow-hidden flex flex-col" data-testid={`storefront-card-${c.id}`}>
      <Link to={`/storefront/${c.id}`} className="block group">
        <div className="aspect-square overflow-hidden bg-gray-100">
          {c.productImageUrl && (
            <img
              src={c.productImageUrl}
              alt={c.productTitle}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          )}
        </div>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/storefront/${c.id}`} className="font-medium text-gray-900 hover:underline line-clamp-2">
            {c.productTitle}
          </Link>
          {isPreOrder ? (
            <span className="lp-badge lp-badge-preorder shrink-0">Pre-order</span>
          ) : isLaunched ? (
            <span className="lp-badge lp-badge-instock shrink-0">In stock</span>
          ) : isClosed ? (
            <span className="lp-badge lp-badge-closed shrink-0">Closed</span>
          ) : (
            <span className="lp-badge lp-badge-voting shrink-0">Launching</span>
          )}
        </div>

        {/* Counter + progress (hidden once it's in stock) */}
        {!isLaunched && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-sm font-semibold text-gray-900 tabular-nums" data-testid={`card-vote-count-${c.id}`}>
              {c.currentVoteCount} <span className="text-gray-400 font-normal">/ {c.voteThreshold} supporters</span>
            </div>
            <div className="text-xs text-gray-500">{pct}%</div>
          </div>
          <ProgressBar value={c.currentVoteCount} max={c.voteThreshold} testId={`card-progress-${c.id}`} />
        </div>
        )}

        {c.preOrderPrice && (
          <div className="text-xs text-[#3A3733] mt-3 uppercase tracking-widest font-bold">
            {isLaunched ? "Ships now · " : isPreOrder ? "Pre-order · " : "Launches at "}${c.preOrderPrice.toFixed(2)}
          </div>
        )}

        {/* CTA */}
        <div className="mt-5">
          {isPreOrder ? (
            <Link
              to={`/storefront/${c.id}`}
              className="lp-btn-primary w-full justify-center"
              data-testid={`card-preorder-btn-${c.id}`}
            >
              <ShoppingBag size={14} /> Pre-order now
            </Link>
          ) : isLaunched ? (
            <Link
              to={`/storefront/${c.id}`}
              className="lp-btn-primary w-full justify-center"
              data-testid={`card-shop-btn-${c.id}`}
            >
              <ShoppingBag size={14} /> Shop now
            </Link>
          ) : isClosed ? (
            <button className="lp-btn-secondary w-full justify-center" disabled data-testid={`card-closed-btn-${c.id}`}>
              <Lock size={14} /> Closed
            </button>
          ) : (
            <LaunchButton
              testId={`card-vote-btn-${c.id}`}
              onClick={onVote}
              launched={voted}
              label="Launch this"
              doneLabel="You're in"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ProductPage({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get("ref") || "";

  const refresh = async () => {
    try {
      const [status, lb] = await Promise.all([
        api.publicStatus(id),
        api.leaderboard(id).catch(() => []),
      ]);
      setData(status);
      setLeaderboard(lb);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [id]); // eslint-disable-line

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Campaign not found</div>;

  // Both voting AND pre_order use the drop-room design (matches the embed widget).
  if (data.status === "pre_order" || data.status === "voting") return <DropRoomPage data={data} onOrdered={refresh} onVoted={refresh} />;

  const refEnabled = !!(data.referralDiscountPercent && data.referralBonusThreshold);

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="storefront-product-page">
      <header className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between border-b border-gray-200">
        <Link to="/storefront" className="font-serif text-2xl tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>demo-shop</Link>
        <Link to="/app" className="text-xs text-gray-500 hover:text-gray-900">← Back to admin</Link>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Product image / video */}
        <div>
          <ProductMedia
            imageUrl={data.productImageUrl}
            productVideoUrl={data.productVideoUrl}
            videoUrl={data.videoUrl}
            title={data.productTitle}
            testId="storefront-product-media"
          />
          {refEnabled && leaderboard.length > 0 && (
            <Leaderboard rows={leaderboard} data={data} />
          )}
        </div>

        {/* Product info */}
        <div className="flex flex-col">
          <div className="lp-section-label">Upcoming launch</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2">{data.productTitle}</h1>
          {data.preOrderPrice && (
            <div className="text-2xl font-medium mt-3 text-gray-900">${data.preOrderPrice.toFixed(2)}</div>
          )}
          <p className="text-sm text-gray-600 mt-4 leading-relaxed">
            Help us decide what to launch next. Drop your email — we&apos;ll only contact you about this product.
          </p>

          {refEnabled && (
            <div className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="referral-banner">
              <Sparkles size={18} className="text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Refer {data.referralBonusThreshold} friends</strong> who launch this with you and unlock{" "}
                <strong>{data.referralDiscountPercent}% off</strong> your pre-order.
              </div>
            </div>
          )}

          {/* Embedded widget */}
          <div className="mt-8">
            {data.status === "voting" ? (
              <VotingWidget data={data} onVoted={refresh} initialRef={refFromUrl} />
            ) : data.status === "pre_order" ? (
              <PreOrderWidget data={data} onOrdered={refresh} />
            ) : (
              <div className="lp-card p-6 text-sm text-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Lock size={16} /> <strong>This launch is closed.</strong>
                </div>
                Thanks for backing this product.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Leaderboard({ rows, data }) {
  return (
    <div className="lp-card p-6 mt-6" data-testid="storefront-leaderboard">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={16} className="text-amber-600" />
        <h3 className="text-sm font-semibold">Top backers</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Most-referring supporters. Unlock {data.referralDiscountPercent}% off at {data.referralBonusThreshold}+ referrals.
      </p>
      <div className="space-y-2">
        {rows.slice(0, 5).map((row, i) => {
          const eligible = row.referralCount >= (data.referralBonusThreshold || Infinity);
          return (
            <div
              key={row.referralCode}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100"
              data-testid={`leaderboard-row-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  i === 0 ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-700"
                }`}>{i + 1}</div>
                <div className="text-sm font-medium text-gray-900">{row.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold tabular-nums">{row.referralCount}</span>
                <span className="text-xs text-gray-500">refs</span>
                {eligible && <span className="lp-badge lp-badge-preorder">Earned</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VotingWidget({ data, onVoted, initialRef }) {
  const [email, setEmail] = useState("");
  const [voted, setVoted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myReferralCode, setMyReferralCode] = useState(null);
  const pct = Math.min(100, Math.round((data.currentVoteCount / Math.max(1, data.voteThreshold)) * 100));
  const refEnabled = !!(data.referralDiscountPercent && data.referralBonusThreshold);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await api.vote(data.id, email, initialRef);
      if (!r.ok && r.reason === "already_voted") {
        toast("You're already in on this launch");
        setMyReferralCode(r.referralCode);
        setVoted(true);
      } else {
        setMyReferralCode(r.referralCode);
        setVoted(true);
        if (r.thresholdHit) {
          toast.success("Threshold hit! Pre-orders are now open.");
        } else {
          toast.success("You're in — thank you!");
        }
        onVoted();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your launch");
    } finally {
      setSubmitting(false);
    }
  };

  const myReferralUrl = myReferralCode
    ? `${window.location.origin}/storefront/${data.id}?ref=${myReferralCode}`
    : null;

  const copyMyReferral = () => {
    if (!myReferralUrl) return;
    navigator.clipboard.writeText(myReferralUrl);
    toast.success("Your referral link copied!");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied — share it to help launch this product!");
  };

  return (
    <div className="lp-card p-6 md:p-7 shadow-[0_2px_24px_rgba(0,0,0,0.06)]" data-testid="voting-widget">
      <div className="flex items-center gap-2">
        <Rocket size={16} />
        <div className="text-sm font-semibold">Help launch this</div>
        {initialRef && !voted && (
          <span className="lp-badge lp-badge-preorder ml-auto" data-testid="ref-applied-badge">
            Referred by {initialRef}
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-900" data-testid="vote-count-display">
            {data.currentVoteCount} / {data.voteThreshold} supporters
          </div>
          <div className="text-xs text-gray-500">{pct}%</div>
        </div>
        <ProgressBar value={data.currentVoteCount} max={data.voteThreshold} testId="voting-progress" />
        <div className="text-xs text-gray-500 mt-3">
          Launches at <strong>{data.voteThreshold}</strong> supporters. {data.estimatedShipDate && (
            <>Est. ship <strong>{data.estimatedShipDate.slice(0, 10)}</strong>.</>
          )}
        </div>
      </div>

      {voted ? (
        <>
          <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-3" data-testid="already-voted-state">
            <Check size={18} className="text-emerald-600 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-emerald-900">You&apos;re in!</div>
              <div className="text-xs text-emerald-700 mt-0.5">We&apos;ll email you the moment this launches.</div>
            </div>
          </div>

          {refEnabled && myReferralUrl && (
            <div className="mt-4 p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-amber-50/40 to-white" data-testid="my-referral-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-amber-600" />
                <div className="text-xs font-semibold">Earn {data.referralDiscountPercent}% off</div>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Refer <strong>{data.referralBonusThreshold}</strong> friends who launch this with you and save{" "}
                <strong>{data.referralDiscountPercent}%</strong> on your pre-order.
              </p>
              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  value={myReferralUrl}
                  className="lp-input flex-1 text-xs font-mono"
                  data-testid="my-referral-url"
                  onFocus={(e) => e.target.select()}
                />
                <button onClick={copyMyReferral} className="lp-btn-primary text-xs" data-testid="copy-my-referral-btn">
                  <Copy size={12} /> Copy
                </button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I'm helping launch ${data.productTitle}. Join me:`)}&url=${encodeURIComponent(myReferralUrl)}`}
                  target="_blank" rel="noreferrer"
                  className="lp-btn-secondary text-xs flex-1 justify-center"
                  data-testid="tweet-my-referral-btn"
                >
                  <Share2 size={12} /> Share on X
                </a>
              </div>
            </div>
          )}
        </>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <label className="lp-label" htmlFor="vote-email">Your email</label>
          <input
            id="vote-email"
            type="email"
            required
            placeholder="you@example.com"
            className="lp-input"
            data-testid="vote-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            type="submit"
            disabled={submitting}
            className={`lp-launch-btn ${submitting ? "is-launching" : ""}`}
            data-testid="vote-submit-btn"
          >
            <span className="smoke" aria-hidden />
            <svg
              className="rocket"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 2c2.5 2.5 4 5.5 4 9 0 1.7-.4 3.3-1 4.7L12 18l-3-2.3c-.6-1.4-1-3-1-4.7 0-3.5 1.5-6.5 4-9Z" />
              <circle cx="12" cy="10" r="1.8" />
              <path d="M8 14l-3 2 1 4 4-2" />
              <path d="M16 14l3 2-1 4-4-2" />
              <path d="M10 19l2 3 2-3" />
            </svg>
            <span className="launch-label">
              {submitting ? "Launching…" : "Launch this product"}
            </span>
          </button>
        </form>
      )}

      {!voted && (
        <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-between">
          <div className="text-xs text-gray-500">Help launch this faster</div>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="lp-btn-secondary text-xs" data-testid="share-copy-btn">
              <Copy size={12} /> Copy link
            </button>
            <a
              href={`https://twitter.com/intent/tweet?text=Help%20launch%20${encodeURIComponent(data.productTitle)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank" rel="noreferrer"
              className="lp-btn-secondary text-xs"
              data-testid="share-twitter-btn"
            >
              <Share2 size={12} /> Share
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function DropRoomPage({ data, onOrdered, onVoted }) {
  const isVoting = data.status === "voting";
  const [related, setRelated] = useState([]);
  useEffect(() => {
    api.publicListCampaigns().then((rows) => {
      setRelated((rows || []).filter((x) => x.id !== data.id && (!x.roundId || x.status === "pre_order")).slice(0, 8));
    }).catch(() => {});
  }, [data.id]);
  if (isVoting) return <DropRoomVoting data={data} related={related} onVoted={onVoted} />;
  return <PreOrderPage data={data} onOrdered={onOrdered} related={related} />;
}

/**
 * Drop-room gallery: main media (video first, else selected image) plus a
 * clickable thumbnail row. Reused by both voting and pre-order layouts so
 * the interaction stays identical whether the product has 2 or 10 images.
 */
function ProductGallery({ data, badge, badgeStyle }) {
  const video = data.videoUrl || data.productVideoUrl;
  const images = (data.productImages && data.productImages.length ? data.productImages : (data.productImageUrl ? [data.productImageUrl] : []));
  const slots = [
    ...(video ? [{ kind: "video", url: video, poster: data.productImageUrl || images[0] }] : []),
    ...images.map((u) => ({ kind: "image", url: u })),
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [data.id]);
  if (slots.length === 0) return null;
  const active = slots[Math.min(idx, slots.length - 1)];
  const defaultBadgeStyle = { position: "absolute", top: 12, left: 12, background: "#0a0a0a", color: "#fff", fontSize: 10, fontWeight: 500, padding: "5px 12px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 5, zIndex: 2 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
      <div style={{ width: "100%", aspectRatio: "4/5", borderRadius: 14, background: "#e8e4db", overflow: "hidden", position: "relative" }}>
        {badge && <div style={{ ...defaultBadgeStyle, ...(badgeStyle || {}) }}>{badge}</div>}
        {active.kind === "video" ? (
          <video src={active.url} autoPlay muted loop playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <img src={active.url} alt={data.productTitle} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
      </div>
      {slots.length > 1 && (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: 2 }} data-testid="storefront-thumbnails">
          {slots.map((s, i) => {
            const active_ = i === Math.min(idx, slots.length - 1);
            const thumbImg = s.kind === "video" ? (s.poster || "") : s.url;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`storefront-thumbnail-${i}`}
                aria-label={s.kind === "video" ? "View video" : `View image ${i + 1}`}
                aria-selected={active_}
                style={{ flex: "0 0 auto", width: 64, height: 64, borderRadius: 10, overflow: "hidden", background: "#e8e4db", border: `1.5px solid ${active_ ? "#0a0a0a" : "transparent"}`, cursor: "pointer", padding: 0, position: "relative", transition: "border-color .15s" }}
              >
                {thumbImg && <img src={thumbImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                {s.kind === "video" && (
                  <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#00000059", color: "#fff", fontSize: 14 }}>▶</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DropRoomVoting({ data, related, onVoted }) {
  const [email, setEmail] = useState(() => localStorage.getItem("launchpad:storefront:email") || "");
  const [emailDraft, setEmailDraft] = useState("");
  const [voted, setVoted] = useState(() => (loadVotedMap()[data.id] ? true : false));
  const [busy, setBusy] = useState(false);

  const goal = data.voteThreshold || 100;
  const backers = data.currentVoteCount || 0;
  const remaining = Math.max(0, goal - backers);
  const pct = goal > 0 ? Math.round(100 * backers / goal) : 0;
  const price = data.preOrderPrice ? Number(data.preOrderPrice).toFixed(2) : "0.00";

  const doVote = async () => {
    let e = email;
    if (!e) {
      e = emailDraft.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Enter a valid email"); return; }
      localStorage.setItem("launchpad:storefront:email", e);
      setEmail(e);
    }
    setBusy(true);
    try {
      const r = await api.vote(data.id, e);
      const map = loadVotedMap(); map[data.id] = true; saveVotedMap(map);
      setVoted(true);
      if (!r.ok && r.reason === "already_voted") toast("You're already backing this");
      else toast.success("Voted!");
      onVoted && onVoted();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not vote"); }
    finally { setBusy(false); }
  };

  return (
    <div className="lp-po min-h-screen" style={{ background: "#f5f3ee", fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }} data-testid="storefront-voting-page">
      <div style={{ padding: "14px 28px", display: "flex", alignItems: "center", borderBottom: ".5px solid #e0ddd6" }}>
        <Link to="/storefront" style={{ fontSize: 12, color: "#888", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="storefront-back-link"><ArrowLeft size={12} /> Back to the Drop Room</Link>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 32, padding: "32px 28px", maxWidth: 960, margin: "0 auto" }} className="lp-po-md-grid">
        <ProductGallery
          data={data}
          badge={<>upcoming launch</>}
          badgeStyle={{ background: "#e8621a", color: "#fff" }}
        />
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: ".14em", color: "#fff", background: "#0a0a0a", padding: "4px 10px", borderRadius: 20, textTransform: "uppercase" }}>{data.roundId ? "The Contest" : "The Drop Room"}</span>
            <span style={{ fontSize: 9, fontWeight: 600, color: "#a35010", background: "#fff3e5", padding: "4px 10px", borderRadius: 20, border: ".5px solid #f0c39a" }}>voting open</span>
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 500, color: "#0a0a0a", lineHeight: 1.2, margin: "0 0 6px", letterSpacing: "-.01em" }}>{data.productTitle}</h1>
          <div style={{ fontSize: 20, fontWeight: 500, color: "#0a0a0a", marginBottom: 6, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>${price}<span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>pre-order price · unlocks at {goal} backers</span></div>
          <div style={{ fontSize: 13, color: "#5f5f5f", lineHeight: 1.7, margin: "14px 0 18px", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#0a0a0a", fontWeight: 500 }}>Help launch this product.</strong>{" "}Vote by dropping your email — no charge, no commitment. When {goal} shoppers back it, pre-order opens automatically and backers ship first.
            {data.productDescription && <><br /><br />{data.productDescription}</>}
          </div>
          <div style={{ background: "#fff3e5", border: ".5px solid #f0c39a", borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e8621a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><ShoppingBag size={14} /></div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#a35010" }}>{backers} / {goal} backers — {remaining} more to launch</div>
              <div style={{ fontSize: 10, color: "#8a5c30", marginTop: 2 }}>Add your email to unlock pre-order for everyone</div>
            </div>
          </div>
          <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 14, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#0a0a0a" }}>{voted ? "You're backing this" : (data.roundId ? "Vote for this product" : "Back this launch")}</div>
              <div style={{ fontSize: 10, color: "#888" }}>{pct}% funded</div>
            </div>
            <div style={{ height: 6, background: "#f3f4f6", borderRadius: 999, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ height: "100%", background: "#e8621a", width: `${Math.min(100, pct)}%`, transition: "width .5s ease" }} />
            </div>
            {!email && !voted && (
              <input type="email" value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} placeholder="you@example.com" data-testid="voting-email-input"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: ".5px solid #e0ddd6", fontSize: 13, marginBottom: 8, fontFamily: "inherit", outline: "none" }} />
            )}
            <button type="button" onClick={doVote} disabled={busy || voted} data-testid="voting-vote-btn"
              style={{ width: "100%", background: voted ? "#2d7a2d" : "#0a0a0a", color: "#fff", border: 0, borderRadius: 10, padding: 13, fontSize: 13, fontWeight: 500, cursor: voted ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginBottom: 8, fontFamily: "inherit" }}>
              {voted ? <><Check size={13} /> You voted — we&apos;ll email you at launch</> : (data.roundId ? <><Check size={13} /> Vote</> : <><ShoppingBag size={13} /> Back this launch — free</>)}
            </button>
            <div style={{ fontSize: 10, color: "#aaa", textAlign: "center" }}>No charge until pre-order opens · one vote per email · unsubscribe anytime</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, marginTop: 14 }}>
            <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 10, padding: "9px 8px", textAlign: "center" }}><div style={{ fontSize: 15, fontWeight: 500, color: "#0a0a0a" }}>{backers}</div><div style={{ fontSize: 8, color: "#aaa", marginTop: 2, letterSpacing: ".06em", textTransform: "uppercase" }}>Backers</div></div>
            <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 10, padding: "9px 8px", textAlign: "center" }}><div style={{ fontSize: 15, fontWeight: 500, color: "#e8621a" }}>{remaining}</div><div style={{ fontSize: 8, color: "#aaa", marginTop: 2, letterSpacing: ".06em", textTransform: "uppercase" }}>To launch</div></div>
            <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 10, padding: "9px 8px", textAlign: "center" }}><div style={{ fontSize: 15, fontWeight: 500, color: "#0a0a0a" }}>{pct}%</div><div style={{ fontSize: 8, color: "#aaa", marginTop: 2, letterSpacing: ".06em", textTransform: "uppercase" }}>Funded</div></div>
          </div>
        </div>
      </div>
      <RelatedProductsStrip related={related} />
    </div>
  );
}

function RelatedProductsStrip({ related }) {
  if (!related || related.length === 0) return null;
  const anyVoting = related.some((x) => x.status !== "pre_order");
  const header = anyVoting ? "Still voting in the Drop Room" : "Still in the Drop Room";
  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 28px 48px" }}>
      <div style={{ background: "#f7f5ef", borderRadius: 16, padding: "20px 20px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "#0a0a0a", letterSpacing: "-.01em" }}>{header}</div>
          <Link to="/storefront" style={{ fontSize: 12, color: "#5f5f5f", textDecoration: "none", padding: "4px 8px", borderRadius: 6 }} data-testid="related-see-all">see all drops →</Link>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }} data-testid="related-products-grid">
          {related.slice(0, 6).map((x) => {
            const backers = x.currentVoteCount || 0;
            const goal = x.voteThreshold || 100;
            const pct = goal > 0 ? Math.min(100, Math.round((100 * backers) / goal)) : 0;
            const priceLbl = x.preOrderPrice ? `$${Number(x.preOrderPrice).toFixed(0)}` : "";
            return (
              <div key={x.id} data-testid={`related-card-${x.id}`} style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <Link to={`/storefront/${x.id}`} style={{ position: "relative", aspectRatio: "16 / 10", background: "#efece4", overflow: "hidden", display: "block", textDecoration: "none" }}>
                  <span style={{ position: "absolute", top: 10, left: 10, background: x.status === "pre_order" ? "#2d7a2d" : "#e8621a", color: "#fff", fontSize: 9, fontWeight: 600, padding: "4px 10px", borderRadius: 20, letterSpacing: ".04em", zIndex: 2 }}>{x.status === "pre_order" ? "pre-order" : "voting"}</span>
                  {x.productImageUrl ? (
                    <img src={x.productImageUrl} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#c8c3b7", fontSize: 32 }}><Rocket size={32} /></div>
                  )}
                </Link>
                <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <Link to={`/storefront/${x.id}`} style={{ fontSize: 13, fontWeight: 500, color: "#0a0a0a", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 34, textDecoration: "none" }}>{x.productTitle}</Link>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 11, color: "#7a7367", fontVariantNumeric: "tabular-nums" }}>
                    <span>{backers} backers</span>
                    <span style={{ fontWeight: 500, color: "#0a0a0a" }}>{priceLbl}</span>
                  </div>
                  <div style={{ height: 3, background: "#eeece5", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: "#0a0a0a", width: `${pct}%`, transition: "width .3s ease" }} />
                  </div>
                  <Link to={`/storefront/${x.id}`} data-testid={`related-launch-${x.id}`} style={{ marginTop: 4, background: "#0a0a0a", color: "#fff", borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 500, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none" }}>
                    <Rocket size={12} /> Launch
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PreOrderPage({ data, onOrdered, related }) {
  const shipMonth = (iso) => {
    if (!iso) return "soon";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "soon";
      return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch { return "soon"; }
  };
  const shipMonthShort = (iso) => {
    if (!iso) return "TBD";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "TBD";
      return d.toLocaleDateString(undefined, { month: "short" });
    } catch { return "TBD"; }
  };
  const backerGoal = data.voteThreshold || 100;
  const backers = data.currentVoteCount || 0;
  const preOrders = data.preOrderCount || 0;
  const ship = shipMonth(data.estimatedShipDate);
  const shipS = shipMonthShort(data.estimatedShipDate);
  const price = data.preOrderPrice ? Number(data.preOrderPrice).toFixed(2) : "0.00";

  const [checkoutOpen, setCheckoutOpen] = useState(false);

  return (
    <div className="lp-po min-h-screen" style={{ background: "#f5f3ee", fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }} data-testid="storefront-preorder-page">
      <div style={{ padding: "14px 28px", display: "flex", alignItems: "center", borderBottom: ".5px solid #e0ddd6" }}>
        <Link to="/storefront" style={{ fontSize: 12, color: "#888", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="storefront-back-link">
          <ArrowLeft size={12} /> Back to the Drop Room
        </Link>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 32, padding: "32px 28px", maxWidth: 960, margin: "0 auto" }} className="lp-po-md-grid">
        <ProductGallery
          data={data}
          badge={<><Check size={10} style={{ color: "#e8621a" }} /> pre-order open</>}
        />
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: ".14em", color: "#fff", background: "#0a0a0a", padding: "4px 10px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 5, textTransform: "uppercase" }}>
              <Rocket size={10} style={{ color: "#e8621a" }} /> {data.roundId ? "The Contest" : "The Drop Room"}
            </span>
            <span style={{ fontSize: 9, fontWeight: 600, color: "#2d7a2d", background: "#eaf3ea", padding: "4px 10px", borderRadius: 20, border: ".5px solid #b8d8b8", display: "inline-flex", alignItems: "center", gap: 4, letterSpacing: ".04em" }}>
              <Check size={10} /> launched
            </span>
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 500, color: "#0a0a0a", lineHeight: 1.2, margin: "0 0 6px", letterSpacing: "-.01em" }}>{data.productTitle}</h1>
          <div style={{ fontSize: 20, fontWeight: 500, color: "#0a0a0a", marginBottom: 6, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            ${price}<span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>pre-order · ships est. {ship}</span>
          </div>
          <div style={{ fontSize: 13, color: "#5f5f5f", lineHeight: 1.7, margin: "14px 0 18px", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#0a0a0a", fontWeight: 500 }}>The community voted this in.</strong>{" "}
            {backerGoal} backers hit the goal and pre-order opened automatically. Order now — limited units, first come first served. Ships to backers first.
            {data.productDescription && <><br /><br />{data.productDescription}</>}
          </div>

          <div style={{ background: "#eaf3ea", border: ".5px solid #b8d8b8", borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#2d7a2d", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Check size={14} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#1a4d1a" }}>Goal reached — {Math.max(backers, backerGoal)} / {backerGoal} backers</div>
              <div style={{ fontSize: 10, color: "#4a7a4a", marginTop: 2 }}>Pre-order is now live · {preOrders} units claimed · limited remaining</div>
            </div>
          </div>

          <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 14, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#0a0a0a" }}>Pre-order now</div>
              <div style={{ fontSize: 10, color: "#888", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Truck size={11} /> est. ships {ship}
              </div>
            </div>
            {checkoutOpen ? (
              <PreOrderWidget data={data} onOrdered={onOrdered} />
            ) : (
              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                data-testid="preorder-open-checkout-btn"
                style={{ width: "100%", background: "#0a0a0a", color: "#fff", border: 0, borderRadius: 10, padding: 13, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginBottom: 8, fontFamily: "inherit" }}
              >
                <ShoppingCart size={13} /> Pre-order · ${price}
              </button>
            )}
            <div style={{ fontSize: 10, color: "#aaa", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "6px 0 2px" }}>
              <Lock size={10} /> Charged now · ships when production completes · free returns
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, paddingTop: 12, borderTop: ".5px solid #f0ede7", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 10, color: "#888", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Flame size={11} style={{ color: "#e8621a" }} /> {preOrders} units claimed so far
              </div>
              <div style={{ fontSize: 10, color: "#2d7a2d", fontWeight: 500 }}>backers ship first</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, marginTop: 14 }}>
            <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 10, padding: "9px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#0a0a0a" }}>{Math.max(backers, backerGoal)}</div>
              <div style={{ fontSize: 8, color: "#aaa", marginTop: 2, letterSpacing: ".06em", textTransform: "uppercase" }}>Backers</div>
            </div>
            <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 10, padding: "9px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#e8621a" }}>{preOrders}</div>
              <div style={{ fontSize: 8, color: "#aaa", marginTop: 2, letterSpacing: ".06em", textTransform: "uppercase" }}>Pre-orders</div>
            </div>
            <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 10, padding: "9px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#0a0a0a" }}>{shipS}</div>
              <div style={{ fontSize: 8, color: "#aaa", marginTop: 2, letterSpacing: ".06em", textTransform: "uppercase" }}>Est. ship</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto 20px", padding: "0 28px" }}>
        <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex" }}>
            {["S", "J", "P", "M"].map((c, i) => (
              <div key={i} style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid #fff", marginRight: -8, background: ["#e8d4c8", "#c8d4e8", "#d4e8c8", "#e8c8d4"][i], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "#666" }}>{c}</div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#0a0a0a", flex: 1, minWidth: 180 }}>
            <div><b style={{ fontWeight: 500 }}>{backerGoal} backers</b> made this happen</div>
            <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>You voted it in — now own it first before it goes public.</div>
          </div>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied — share the launch!"); }}
            style={{ fontSize: 10, color: "#555", background: "#f5f3ee", border: ".5px solid #e0ddd6", borderRadius: 20, padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "inherit" }}
          >
            <Share2 size={11} /> Share the launch
          </button>
        </div>
      </div>

      <div style={{ height: ".5px", background: "#e0ddd6", maxWidth: 960, margin: "0 auto 20px" }} />

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 28px 32px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }} className="lp-po-details-md">
          <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#0a0a0a", marginBottom: 10, display: "flex", alignItems: "center", gap: 7, textTransform: "uppercase", letterSpacing: ".05em" }}>
              <Info size={13} style={{ color: "#888" }} /> Product details
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              <li style={{ fontSize: 11, color: "#0a0a0a", fontWeight: 500, display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#2d7a2d", fontWeight: 700 }}>✓</span>Community voted this in — shipped as pre-order after goal hit</li>
              <li style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>Limited units — first come, first served</li>
              <li style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>Backers get priority shipping over public pre-orders</li>
              {data.productDescription && String(data.productDescription).split("\n").slice(0, 3).map((line, i) => (
                <li key={i} style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>{line}</li>
              ))}
            </ul>
          </div>
          <div style={{ background: "#fff", border: ".5px solid #e0ddd6", borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#0a0a0a", marginBottom: 10, display: "flex", alignItems: "center", gap: 7, textTransform: "uppercase", letterSpacing: ".05em" }}>
              <Truck size={13} style={{ color: "#888" }} /> Pre-order timeline
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              <li style={{ fontSize: 11, color: "#0a0a0a", fontWeight: 500, display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#2d7a2d", fontWeight: 700 }}>✓</span>Backers ship before public pre-orders</li>
              <li style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>Payment taken now at checkout</li>
              <li style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>Production begins immediately</li>
              <li style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>Est. ship {ship}</li>
              <li style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "baseline", gap: 8, lineHeight: 1.5 }}><span style={{ color: "#ccc" }}>—</span>Free returns on delivery</li>
            </ul>
            <div style={{ fontSize: 10, color: "#aaa", marginTop: 10, lineHeight: 1.5 }}>If production is delayed we&apos;ll notify you immediately with an updated timeline.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreOrderWidget({ data, onOrdered }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [voterState, setVoterState] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  // Auto-lookup eligibility when email is a complete address
  useEffect(() => {
    const isComplete = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isComplete) {
      setVoterState(null);
      return;
    }
    setLookingUp(true);
    const t = setTimeout(async () => {
      try {
        const s = await api.voterLookup(data.id, email);
        setVoterState(s);
      } catch {
        setVoterState(null);
      } finally {
        setLookingUp(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [email, data.id]);

  const basePrice = data.preOrderPrice || 0;
  const discountPct = voterState?.discountEligible ? (voterState.discountPercent || 0) : 0;
  const finalPrice = +(basePrice * (1 - discountPct / 100)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const po = await api.preorder(data.id, email, name);
      setLastOrder(po);
      // If Shopify returned a real checkout URL, redirect the customer to pay via Shopify.
      if (po.checkoutUrl) {
        toast.success("Redirecting to Shopify checkout…");
        // Brief delay so the toast shows, then full-page redirect on the TOP window
        // (so if we're embedded in an iframe, checkout takes over the whole browser tab,
        // not just the iframe area).
        setTimeout(() => {
          try {
            window.top.location.href = po.checkoutUrl;
          } catch {
            window.location.href = po.checkoutUrl;
          }
        }, 800);
        return;
      }
      // Fallback (mock mode / no Shopify connected) — show the local confirmation state
      setDone(true);
      toast.success("Pre-order confirmed!");
      onOrdered();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place pre-order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-card p-6 md:p-7 shadow-[0_2px_24px_rgba(0,0,0,0.06)]" data-testid="preorder-widget">
      <div className="flex items-center gap-2">
        <ShoppingBag size={16} className="text-emerald-700" />
        <div className="text-sm font-semibold">Now available for pre-order</div>
      </div>
      <div className="mt-4 text-sm text-gray-700">
        Est. ship date: <strong>{data.estimatedShipDate ? data.estimatedShipDate.slice(0, 10) : "TBA"}</strong>
        {data.remainingSlots !== null && data.remainingSlots !== undefined && (
          <span className="ml-2 text-xs text-gray-500">
            · <strong>{data.remainingSlots}</strong> slots left
          </span>
        )}
      </div>

      {done ? (
        <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="preorder-confirmed-state">
          <div className="flex items-center gap-2 text-emerald-900 font-medium">
            <Check size={18} /> Pre-order placed
          </div>
          <div className="text-xs text-emerald-700 mt-1">
            We&apos;ll email <strong>{email}</strong> when your order ships.
          </div>
          {lastOrder && lastOrder.discountApplied > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-200 text-xs text-emerald-800">
              Referral discount applied: <strong>−${lastOrder.discountApplied.toFixed(2)}</strong>{" "}
              · Charged <strong>${lastOrder.amount.toFixed(2)}</strong>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label className="lp-label" htmlFor="po-name">Name</label>
            <input
              id="po-name" className="lp-input" required
              data-testid="preorder-name-input"
              value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Carter"
            />
          </div>
          <div>
            <label className="lp-label" htmlFor="po-email">Email</label>
            <input
              id="po-email" type="email" className="lp-input" required
              data-testid="preorder-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            />
            {voterState?.hasVoted && (
              <div className="mt-2 text-xs text-gray-600" data-testid="voter-state-info">
                You referred <strong>{voterState.referralCount}</strong> friend(s)
                {voterState.bonusThreshold && (
                  <> · Need <strong>{voterState.bonusThreshold}</strong> for the discount</>
                )}
              </div>
            )}
            {lookingUp && <div className="mt-1 text-xs text-gray-400">Checking your referral status…</div>}
          </div>

          {voterState?.discountEligible && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="discount-applied-banner">
              <Trophy size={18} className="text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Top referrer discount unlocked!</strong>
                <div className="text-xs mt-0.5">
                  {voterState.discountPercent}% off · You save{" "}
                  <strong>${(basePrice - finalPrice).toFixed(2)}</strong>.
                </div>
              </div>
            </div>
          )}

          <div className="flex items-baseline justify-between pt-2">
            <div className="text-sm text-gray-500">Total</div>
            <div className="flex items-baseline gap-2">
              {discountPct > 0 && (
                <span className="text-sm text-gray-400 line-through" data-testid="base-price-strike">
                  ${basePrice.toFixed(2)}
                </span>
              )}
              <span className="text-xl font-semibold" data-testid="final-price">${finalPrice.toFixed(2)}</span>
            </div>
          </div>

          <button
            type="submit"
            className="lp-btn-primary w-full justify-center"
            data-testid="preorder-submit-btn"
            disabled={submitting}
          >
            {submitting ? "Redirecting to checkout…" : `Pre-order & pay — $${finalPrice.toFixed(2)}`}
          </button>
          <div className="text-[11px] text-gray-500 text-center flex items-center justify-center gap-1.5">
            <Lock size={10} /> Secure checkout via Shopify · Apple Pay, Shop Pay, cards
          </div>
        </form>
      )}
    </div>
  );
}
